import { useApiClient } from "@/hooks/useApiClient";
import useSWR from "swr";
import {
  useBannerActions,
  type BannerAction,
  type BannerPosition,
} from "@/features/banner/api/useBannerActions";
import { useFeatureFlags } from "@/configs/featureFlag";

export interface FloatingBannerItem {
  id: number;
  imageUrl: string;
  linkUrl: string;
  title: string;
  description: string;
  displayOrder: number;
}

interface FloatingBannerListResponse {
  banners: FloatingBannerItem[];
}

export type { BannerAction, BannerPosition };

export const useFloatingBanners = (
  position: BannerPosition = "floating"
): {
  banners: FloatingBannerItem[];
  recordAction: (id: number, action: BannerAction) => Promise<void>;
  removeBannerFromCache: (id: number) => void;
} => {
  const { apiClient } = useApiClient();
  const { recordAction } = useBannerActions();
  const url = `/floating-banner/?position=${position}`;

  // 広告バナー機能の表示制御（LaunchDarkly フィーチャーフラグ）。
  // promotionalBanners が true のときだけ全枠（ピックアップ/フローティング/
  // ログインモーダル）を表示する。既定（フラグ未定義・false）は非表示。
  // 他のロールアウトフラグと同じく === true 判定。無効時は取得自体を
  // 行わない（SWR key を null にする）。
  const { promotionalBanners } = useFeatureFlags();
  const enabled = promotionalBanners === true;

  const fetcher = async (): Promise<FloatingBannerListResponse> => {
    return (await apiClient.get<FloatingBannerListResponse>(url)).data;
  };

  // 位置ごとにキャッシュを分ける。
  // バナーは補助的な表示であり、取得に失敗しても画面本体の機能には影響しない。
  // そのためエラーは握りつぶし（バナーを出さない）、ログだけ残す。
  const { data, mutate } = useSWR(enabled ? url : null, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    onError: (error) => {
      console.error(
        `floating banner fetch failed (position=${position})`,
        error
      );
    },
  });

  // 非表示/恒久非表示が確定したバナーを SWR キャッシュからも除外する。
  // これをしないと、画面を離れて戻った際にコンポーネント内の非表示状態が
  // 初期化され、古いバナーが再表示される。再取得は行わない（revalidate=false）。
  const removeBannerFromCache = (id: number): void => {
    void mutate(
      (current) =>
        current
          ? { banners: current.banners.filter((b) => b.id !== id) }
          : current,
      { revalidate: false }
    );
  };

  return {
    banners: data?.banners ?? [],
    recordAction,
    removeBannerFromCache,
  };
};
